import type { Cleanup } from '../helpers/cleanup'
import {
  finishTreeHydration,
  revealSelectedTreeItem,
  selectedTreeItemId,
  syncTreeExpansion
} from '../helpers/tree'
import type { ViewHook } from '../helpers/utils'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type TreeController = Readonly<{
  cleanup: Cleanup
  sync: () => void
}>

type TreeHook = ViewHook & {
  _treeController?: TreeController
  _treeCleanup?: Cleanup
}

// Private functions -----------------------------------------------------------

// Owns hydration and selection updates for one upgraded tree.
const setupTree = (element: HTMLElement): TreeController => {
  const controller = new AbortController()
  let revision = 0
  let selectedId: string | null = null

  // Reveals selection only after the newest active hydration finishes.
  const finish = async (hydration: Promise<void>): Promise<void> => {
    const currentRevision = ++revision
    await hydration

    if (controller.signal.aborted || currentRevision !== revision) {
      return
    }

    const nextSelectedId = selectedTreeItemId(element)

    if (nextSelectedId !== selectedId) {
      selectedId = nextSelectedId
      revealSelectedTreeItem(element)
    }
  }

  // Reports failures only while this tree still owns its pending work.
  const reportFailure = (error: unknown): void => {
    if (!controller.signal.aborted) {
      console.error('Unable to hydrate tree', error)
    }
  }

  // Waits for generic live props to finish before reading a patched tree.
  const sync = (): void => {
    queueMicrotask(() => {
      if (!controller.signal.aborted) {
        finish(finishTreeHydration(element, controller.signal)).catch(
          reportFailure
        )
      }
    })
  }

  finish(syncTreeExpansion(element, controller.signal)).catch(reportFailure)

  return { cleanup: () => controller.abort(), sync }
}

// Public hook -----------------------------------------------------------------

export const FacadeTree = {
  // Waits for definitions before hydrating server expansion and selection.
  mounted(this: TreeHook): void {
    this._treeCleanup?.()
    this._treeCleanup = setupPageComponent(this.el, (element) => {
      const controller = setupTree(element)
      this._treeController = controller

      return () => {
        controller.cleanup()
        this._treeController = undefined
      }
    })
  },

  // Defers post-patch work until generic live props have been applied.
  updated(this: TreeHook): void {
    this._treeController?.sync()
  },

  // Invalidates pending hydration work when the hook is removed.
  destroyed(this: TreeHook): void {
    this._treeCleanup?.()
    this._treeCleanup = undefined
  }
}
